import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Star, MessageSquare, Pencil, ShieldAlert, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

const MAX_COMMENT = 2000;
const EDIT_WINDOW_MS = 14 * 24 * 60 * 60 * 1000;

type Review = {
  id: string;
  order_id: string;
  buyer_id: string;
  seller_id: string;
  rating: number;
  comment: string | null;
  is_hidden: boolean;
  created_at: string;
};

type OrderLike = {
  id: string;
  buyer_id: string;
  status: string;
  has_active_dispute: boolean;
  delivery_confirmed_at: string | null;
};

export function BuyerReviewActions({
  order,
  currentUserId,
}: {
  order: OrderLike;
  currentUserId: string | null | undefined;
}) {
  const qc = useQueryClient();
  const isBuyer = !!currentUserId && order.buyer_id === currentUserId;
  const orderEligible =
    isBuyer &&
    !!order.delivery_confirmed_at &&
    !["refunded", "cancelled"].includes(order.status) &&
    !order.has_active_dispute;

  const { data: review, isLoading } = useQuery({
    queryKey: ["order-review", order.id],
    enabled: isBuyer,
    queryFn: async (): Promise<Review | null> => {
      const { data, error } = await supabase
        .from("reviews" as never)
        .select("id,order_id,buyer_id,seller_id,rating,comment,is_hidden,created_at")
        .eq("order_id", order.id)
        .maybeSingle();
      if (error) throw error;
      return (data as Review | null) ?? null;
    },
  });

  const [open, setOpen] = useState(false);
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [comment, setComment] = useState("");
  const isEdit = !!review && !review.is_hidden;

  useEffect(() => {
    if (!open) return;
    if (isEdit && review) {
      setRating(review.rating);
      setComment(review.comment ?? "");
    } else {
      setRating(0);
      setComment("");
    }
    setHoverRating(0);
  }, [open, isEdit, review]);

  const submit = useMutation({
    mutationFn: async () => {
      if (rating < 1 || rating > 5) throw new Error("Please choose a star rating");
      if (comment.length > MAX_COMMENT) throw new Error("Comment is too long");
      if (isEdit && review) {
        const { error } = await supabase.rpc(
          "update_own_review" as never,
          {
            _review_id: review.id,
            _rating: rating,
            _comment: comment.trim() ? comment.trim() : null,
          } as never,
        );
        if (error) throw error;
      } else {
        const { error } = await supabase.rpc(
          "submit_review" as never,
          {
            _order_id: order.id,
            _rating: rating,
            _comment: comment.trim() ? comment.trim() : null,
          } as never,
        );
        if (error) throw error;
      }
    },
    onSuccess: () => {
      toast.success(isEdit ? "Feedback updated" : "Thanks for your feedback");
      setOpen(false);
      qc.invalidateQueries({ queryKey: ["order-review", order.id] });
    },
    onError: (e: Error) => toast.error(e.message || "Could not save feedback"),
  });

  if (!isBuyer) return null;
  if (isLoading) {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
        <Loader2 className="h-3 w-3 animate-spin" />
      </span>
    );
  }

  // Existing hidden review — show buyer-facing status; no reason exposed
  if (review?.is_hidden) {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-amber-300">
        <ShieldAlert className="h-3.5 w-3.5" aria-hidden />
        Your feedback is under review
      </span>
    );
  }

  // Has a visible review
  if (review) {
    const withinEditWindow =
      new Date(review.created_at).getTime() > Date.now() - EDIT_WINDOW_MS;
    return (
      <div className="flex flex-col items-end gap-1">
        <span className="inline-flex items-center gap-1 text-xs text-emerald-300">
          Your feedback:
          <StarRow value={review.rating} />
        </span>
        {review.comment && (
          <p className="text-xs text-muted-foreground max-w-[240px] text-right line-clamp-2">
            “{review.comment}”
          </p>
        )}
        {withinEditWindow && (
          <>
            <Button size="sm" variant="ghost" onClick={() => setOpen(true)}>
              <Pencil className="h-3.5 w-3.5 mr-1" /> Edit feedback
            </Button>
            <ReviewDialog
              open={open}
              onOpenChange={setOpen}
              rating={rating}
              hoverRating={hoverRating}
              setRating={setRating}
              setHoverRating={setHoverRating}
              comment={comment}
              setComment={setComment}
              submitting={submit.isPending}
              onSubmit={() => submit.mutate()}
              mode="edit"
            />
          </>
        )}
      </div>
    );
  }

  // No review yet — only show button if all eligibility rules pass
  if (!orderEligible) return null;

  return (
    <>
      <Button size="sm" variant="outline" onClick={() => setOpen(true)}>
        <MessageSquare className="h-4 w-4 mr-1" /> Leave feedback
      </Button>
      <ReviewDialog
        open={open}
        onOpenChange={setOpen}
        rating={rating}
        hoverRating={hoverRating}
        setRating={setRating}
        setHoverRating={setHoverRating}
        comment={comment}
        setComment={setComment}
        submitting={submit.isPending}
        onSubmit={() => submit.mutate()}
        mode="create"
      />
    </>
  );
}

function StarRow({ value }: { value: number }) {
  return (
    <span className="inline-flex" aria-label={`${value} out of 5 stars`}>
      {[1, 2, 3, 4, 5].map((i) => (
        <Star
          key={i}
          className={cn(
            "h-3.5 w-3.5",
            i <= value ? "text-gold fill-gold" : "text-muted-foreground/40",
          )}
          aria-hidden
        />
      ))}
    </span>
  );
}

function ReviewDialog({
  open,
  onOpenChange,
  rating,
  hoverRating,
  setRating,
  setHoverRating,
  comment,
  setComment,
  submitting,
  onSubmit,
  mode,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  rating: number;
  hoverRating: number;
  setRating: (n: number) => void;
  setHoverRating: (n: number) => void;
  comment: string;
  setComment: (v: string) => void;
  submitting: boolean;
  onSubmit: () => void;
  mode: "create" | "edit";
}) {
  const displayRating = hoverRating || rating;
  const disabled = submitting || rating < 1 || comment.length > MAX_COMMENT;
  return (
    <Dialog open={open} onOpenChange={(v) => (!submitting ? onOpenChange(v) : undefined)}>
      <DialogContent className="sm:max-w-md w-[calc(100vw-2rem)]">
        <DialogHeader>
          <DialogTitle>{mode === "edit" ? "Edit your feedback" : "Leave feedback"}</DialogTitle>
          <DialogDescription>
            Rate your experience with this seller. Ratings help other buyers on BidNova Auctions.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div>
            <Label className="text-xs uppercase tracking-widest text-muted-foreground">Rating</Label>
            <div
              className="mt-2 flex items-center gap-1"
              role="radiogroup"
              aria-label="Star rating"
              onMouseLeave={() => setHoverRating(0)}
            >
              {[1, 2, 3, 4, 5].map((i) => (
                <button
                  key={i}
                  type="button"
                  role="radio"
                  aria-checked={rating === i}
                  aria-label={`${i} star${i === 1 ? "" : "s"}`}
                  className="rounded-md p-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-gold"
                  onClick={() => setRating(i)}
                  onMouseEnter={() => setHoverRating(i)}
                  onFocus={() => setHoverRating(i)}
                  onBlur={() => setHoverRating(0)}
                  onKeyDown={(e) => {
                    if (e.key === "ArrowRight" || e.key === "ArrowUp") {
                      e.preventDefault();
                      setRating(Math.min(5, (rating || 0) + 1));
                    } else if (e.key === "ArrowLeft" || e.key === "ArrowDown") {
                      e.preventDefault();
                      setRating(Math.max(1, (rating || 1) - 1));
                    }
                  }}
                >
                  <Star
                    className={cn(
                      "h-7 w-7 transition-colors",
                      i <= displayRating ? "text-gold fill-gold" : "text-muted-foreground/40",
                    )}
                    aria-hidden
                  />
                </button>
              ))}
            </div>
          </div>

          <div>
            <Label htmlFor="review-comment">Comment (optional)</Label>
            <Textarea
              id="review-comment"
              value={comment}
              onChange={(e) => setComment(e.target.value.slice(0, MAX_COMMENT))}
              placeholder="How was packaging, communication, delivery?"
              rows={5}
              maxLength={MAX_COMMENT}
              className="mt-1"
            />
            <p
              className={cn(
                "mt-1 text-right text-xs",
                comment.length >= MAX_COMMENT
                  ? "text-red-400"
                  : "text-muted-foreground",
              )}
              aria-live="polite"
            >
              {comment.length} / {MAX_COMMENT}
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button
            className="bg-gold-gradient hover:opacity-90"
            onClick={onSubmit}
            disabled={disabled}
          >
            {submitting && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
            {mode === "edit" ? "Save changes" : "Submit feedback"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
